import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { ConfigurationsService, ConfiguratorOptions, ModalService, StepComponent } from '@icc/configurator/shared';
import { iccListTab, iccSelect, iccListItem } from '@icc/configurator/ui';
import { WindowFacade } from '../+state/window-door.facade';
import { Observable, from, Subscription, combineLatest } from 'rxjs';
import { FillingsService } from '../modules/glazings/fillings.service';
import { ActiveSash, DoorActiveConfiguration, WindowActiveConfiguration } from '@icc/window-door/types';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared';
import { IccFilling } from '@icc/window-door/types';
import { BrowserGlazingBeadsService } from '../modules/glazings/glazing-beads.service';
import { WarmEdgesService } from '../modules/glazings/warm-edges.service';
import { SealColorsService } from '../modules/glazings/seal-colors.service';
import { SiliconeColorsService } from '../modules/glazings/silicone-colors.service';
import { GlazingLayoutPageComponent } from '../glazing-layout-page/glazing-layout-page.component';
import { FillingsFacade } from '../glazings/fillings.facade';
import { map, withLatestFrom } from 'rxjs/operators';
import { _ } from '@icc/shared/helpers';

@Component({
    selector: 'icc-glazings',
    templateUrl: './glazings.component.html',
    styleUrls: ['./glazings.component.scss'],
    standalone: false
})
export class GlazingsComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('WINDOW|Szyby i wypełnienia');
    static stepIcon = {
        fontSet: 'icc-icons',
        fontIcon: 'icc-icons-glazing',
    };

    public configurator = 'window';
    public stepId = 'glazings';
    public title = _('WINDOW|Szyby i wypełnienia');
    public options = [
        {
            title: _('WINDOW|Wypełnienie per kwatera'),
            component: GlazingLayoutPageComponent,
        },
    ];

    public glazingSpacer$ = this.windowFacade.selectedGlazingSpacer$;
    public glazingBead$ = this.windowFacade.selectedGlazingBead$;
    public glazingBeadInSashes$ = this.windowFacade.selectedGlazingBeadInSashes$;
    public glazingBeadInFix$ = this.windowFacade.selectedGlazingBeadInFixed$;
    public sealColor$ = this.windowFacade.selectedSealColor$;
    public siliconeColor$ = this.windowFacade.selectedSiliconeColor$;

    availableSealColor$ = this.windowFacade.system$.pipe(
        map(
            system =>
                this.config().IccConfig.Configurators.sealColorSelect
                || (this.config().IccConfig.Configurators.siliconeColor && system.type === 'wood')
        )
    );
    availableSiliconeColor$ = this.windowFacade.system$.pipe(
        map(system => this.config().IccConfig.Configurators.siliconeColor && system.type === 'wood')
    );
    availableGlazingBead$ = this.windowFacade.oneGlazingBead$;
    availableGlazingBeadSashes$ = this.windowFacade.oneGlazingBeadSashes$;
    availableGlazingBeadFix$ = this.windowFacade.oneGlazingBeadFix$;

    hasWarmEdge$ = this.windowFacade.hasWarmEdge$;

    oneFillingBox?: ReturnType<FillingsFacade['fillingBox']>;
    doorActiveFillingBox?: ReturnType<FillingsFacade['fillingBox']>;
    doorPassiveFillingBox?: ReturnType<FillingsFacade['fillingBox']>;
    windowFillingBox?: ReturnType<FillingsFacade['fillingBox']>;

    private subscriptions: Subscription[] = [];

    static stepEnable = (conf: WindowActiveConfiguration, options: ConfiguratorOptions) =>
        conf.type !== 'door' || (!conf.System || !Boolean(conf.System.door_type));

    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private windowFacade: WindowFacade,
        public fillingsFacade: FillingsFacade,
        private modalService: ModalService,
        private fillingsService: FillingsService,
        private configurationsService: ConfigurationsService<'window' | 'door'>,
        private glazingBeadsService: BrowserGlazingBeadsService,
        private warmEdgesService: WarmEdgesService,
        private sealColorsService: SealColorsService,
        private siliconeColorsService: SiliconeColorsService
    ) {
        super();
    }

    openModalDecoPanelOptions(field: string | undefined | ActiveSash) {
        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.fillingsService.openModalPanelOptions(
                field,
                this.configurationsService.conf.Current
            );
        } else {
            this.modalService.open({
                pageComponent: GlazingLayoutPageComponent
            });
        }
    }

    openModalFilling(field: string | undefined | ActiveSash, sash?: ActiveSash) {
        const {matchingFillings} = this.fillingsService.getMatchingFillingsForModal(field, this.configurationsService.conf.Current, sash);
        if(matchingFillings.length > 0) {
            this.fillingsService.openModal(
                field,
                this.configurationsService.conf.Current,
                this.configurationsService.conf.Default,
                sash
            );
        } else {
            this.modalService.open({
                pageComponent: GlazingLayoutPageComponent
            });
        }
    }

    isFillingSelectionDisabled(field: string | undefined | ActiveSash, sash?: ActiveSash) {
        const { matchingFillings, selectedFilling } = this.fillingsService.getMatchingFillingsForModal(
            field,
            this.configurationsService.conf.Current,
            sash
        );
        return matchingFillings.length > 0
            && !matchingFillings.some(filling => Number(filling.id) !== Number(selectedFilling));
    }

    openModalGlazingBead(field: 'sashes' | 'fix' | undefined | ActiveSash, sash?: ActiveSash) {
        this.glazingBeadsService.openModalGlazingBead(
            this.configurationsService.conf.Current,
            field,
            sash
        );
    }

    isGlazingBeadSelectionDisabled(field: 'sashes' | 'fix' | undefined | ActiveSash, sash?: ActiveSash) {
        const { glazingBeads, selectedGlazingBead } = this.glazingBeadsService.getGlazingBeadSelection(
            this.configurationsService.conf.Current,
            field,
            sash
        );
        return !glazingBeads.some(bead => Number(bead.id) !== Number(selectedGlazingBead?.profileId));
    }

    isWarmEdgeSelectionDisabled() {
        const selectedId = this.configurationsService.conf.Current.WarmEdge?.id;
        return !this.warmEdgesService.warmEdges.some(edge => Number(edge.id) !== Number(selectedId));
    }

    isSealColorSelectionDisabled() {
        const selectedId = this.configurationsService.conf.Current.SealColor?.id;
        return !this.sealColorsService.colors.some(color => Number(color.id) !== Number(selectedId));
    }

    isSiliconeColorSelectionDisabled() {
        const selectedId = this.configurationsService.conf.Current.SiliconeColor?.id;
        return !this.siliconeColorsService.colors.some(color => Number(color.id) !== Number(selectedId));
    }

    openModalWarmEdge() {
        this.warmEdgesService.openModal(
            this.configurationsService.conf.Current,
            this.configurationsService.conf.Default
        );
    }

    /**
     * Funkcja otwierajaca okno modal z kolorem uszczelek
     */
    openModalSealColor() {
        this.sealColorsService.openModal(
            this.configurationsService.conf.Current,
            this.configurationsService.conf.Default
        );
    }

    /**
     * Funkcja otwierajaca okno modal z kolorem uszczelek
     */
    openModalSiliconeColor() {
        this.siliconeColorsService.openModal(
            this.configurationsService.conf.Current,
            this.configurationsService.conf.Default
        );
    }

    ngOnInit() {
        this.subscriptions.push(
            this.fillingsFacade.oneFillingBox$.subscribe(box => {
                this.oneFillingBox = box;
            })
        );
        this.subscriptions.push(
            this.fillingsFacade.doorActiveFillingBox$.subscribe(box => {
                this.doorActiveFillingBox = box;
            })
        );
        this.subscriptions.push(
            this.fillingsFacade.doorPassiveFillingBox$.subscribe(box => {
                this.doorPassiveFillingBox = box;
            })
        );
        this.subscriptions.push(
            this.fillingsFacade.windowFillingBox$.subscribe(box => {
                this.windowFillingBox = box;
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
